/**
 * Director Console — extracted sub-components.
 *
 * These components used to live inline in `pages/DirectorConsole.jsx`
 * (which had grown to ~1400 lines). They've been pulled out into this
 * shared module so the console page can stay focused on layout + state
 * orchestration while these reusable presentational pieces live with
 * the rest of the broadcaster components.
 *
 * Exports:
 *   MonitorTile     — placeholder tile in the Monitors column
 *   BackstagePanel  — collapsible guest waiting-room block
 *   RecordMenu      — Record / dropdown for HD · HD+Cloud · Live Cloud
 *   CameraPicker    — `enumerateDevices` <select> for the program camera
 *   ConsoleMoreMenu — overflow popover for secondary toolbar actions
 *   CrewModeSelect  — Tap In Crew bot-chatter mode selector
 */
import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Users,
  MessageSquare,
  MessageSquareOff,
  Wand2,
  Circle as CircleIcon,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* ── MonitorTile ──────────────────────────────────────────────────── */
export function MonitorTile({ label, badge, badgeColor = "bg-white/10 text-white", children }) {
  return (
    <div
      data-testid={`monitor-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="relative aspect-video rounded-lg border border-white/10 bg-gradient-to-br from-white/5 to-white/0 overflow-hidden"
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-black">{label}</div>
      </div>
      {badge && (
        <span className={`absolute top-1 left-1 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeColor}`}>
          {badge}
        </span>
      )}
      {children && <div className="absolute bottom-1 left-1 right-1">{children}</div>}
    </div>
  );
}

/* ── BackstagePanel ───────────────────────────────────────────────── */
export function BackstagePanel({ guests = [] }) {
  const [open, setOpen] = useState(true);
  const waiting = guests.filter((g) => g.status === "waiting").length;
  return (
    <section data-testid="monitors-backstage" className="mt-1 bg-black/40 border border-white/10 rounded-lg p-2">
      <button
        data-testid="backstage-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-white/60 hover:text-white"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Users className="w-3 h-3 text-tv-accent" /> Back Stage
        {waiting > 0 && (
          <span data-testid="backstage-waiting-badge" className="ml-auto bg-tv-live text-white text-[9px] font-black px-1.5 py-0.5 rounded-full tv-pulse">
            {waiting}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-2 space-y-1.5">
          {guests.length === 0 ? (
            <div data-testid="backstage-empty" className="text-[10px] text-white/40 italic">No guests waiting</div>
          ) : (
            guests.slice(0, 4).map((g) => (
              <MonitorTile
                key={g.id}
                label={g.user_name || "Guest"}
                badge={(g.status || "wait").slice(0, 4).toUpperCase()}
                badgeColor={g.status === "onscreen" ? "bg-tv-live text-white" : g.status === "approved" ? "bg-emerald-500 text-white" : "bg-sky-500 text-white"}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
}

/* ── RecordMenu ───────────────────────────────────────────────────── */
export function RecordMenu({ recording, onStart, onStop }) {
  const [open, setOpen] = useState(false);
  const mmss = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };
  const [, force] = useState(0);
  useEffect(() => {
    if (!recording) return;
    const h = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(h);
  }, [recording]);

  if (recording) {
    return (
      <button
        data-testid="console-record-stop"
        onClick={onStop}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-tv-live text-white hover:bg-red-600 shadow-[0_0_12px_rgba(255,0,64,0.4)]"
        title="Stop recording"
      >
        <CircleIcon className="w-3.5 h-3.5 fill-current tv-pulse" />
        REC {mmss(Date.now() - recording.startedAt)} · {recording.mode === "cloud" ? "HD+Cloud" : "HD"}
      </button>
    );
  }
  return (
    <div className="relative">
      <button
        data-testid="console-record-btn"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-white border border-white/10 hover:bg-white/10"
        title="Record stream (premium)"
      >
        <CircleIcon className="w-3.5 h-3.5 text-tv-live" /> Record
      </button>
      {open && (
        <div data-testid="console-record-menu" className="absolute right-0 top-full mt-2 w-56 bg-[#0A0D14] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50">
          <button
            data-testid="record-option-local"
            onClick={() => { setOpen(false); onStart("local"); }}
            className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/10 flex items-start gap-3"
          >
            <CircleIcon className="w-3 h-3 mt-1 text-tv-live fill-current" />
            <div>
              <div className="font-bold text-white text-xs">Hard Drive</div>
              <div className="text-[10px] text-white/50 mt-0.5">Downloads WebM to your device when you stop.</div>
            </div>
          </button>
          <button
            data-testid="record-option-cloud"
            onClick={() => { setOpen(false); onStart("cloud"); }}
            className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/10 flex items-start gap-3"
          >
            <CircleIcon className="w-3 h-3 mt-1 text-tv-accent fill-current" />
            <div>
              <div className="font-bold text-white text-xs flex items-center gap-1">
                Hard Drive + Cloud
                <span className="text-[8px] bg-tv-accent text-black font-black px-1.5 py-0.5 rounded">PRO</span>
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">Saves locally AND uploads to your Tap IN library.</div>
            </div>
          </button>
          <button
            data-testid="record-option-live-cloud"
            onClick={() => { setOpen(false); onStart("live_cloud"); }}
            className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-start gap-3"
          >
            <CircleIcon className="w-3 h-3 mt-1 text-emerald-400 fill-current" />
            <div>
              <div className="font-bold text-white text-xs flex items-center gap-1">
                Live Cloud
                <span className="text-[8px] bg-emerald-500 text-black font-black px-1.5 py-0.5 rounded">PRO+</span>
              </div>
              <div className="text-[10px] text-white/50 mt-0.5">Streams 2-second chunks live — crash-proof, nothing lost.</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

/* ── CameraPicker ─────────────────────────────────────────────────── */
export function CameraPicker({ onSelect }) {
  const [devices, setDevices] = useState([]);
  const [active, setActive] = useState("");
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (!alive) return;
        setDevices(all.filter((d) => d.kind === "videoinput"));
      } catch { /* permissions denied */ }
    }
    load();
    navigator.mediaDevices?.addEventListener?.("devicechange", load);
    return () => { alive = false; navigator.mediaDevices?.removeEventListener?.("devicechange", load); };
  }, []);
  return (
    <div className="mb-1">
      <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-1">Camera</div>
      <select
        data-testid="monitor-camera-picker"
        value={active}
        onChange={(e) => {
          setActive(e.target.value);
          const label = devices.find((d) => d.deviceId === e.target.value)?.label?.slice(0, 18) || "Camera";
          onSelect?.(e.target.value, label);
        }}
        className="w-full bg-black/60 border border-white/10 rounded px-2 py-1.5 text-[10px] text-white focus:border-tv-accent outline-none"
      >
        <option value="">Auto</option>
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
        ))}
      </select>
    </div>
  );
}

/* ── ConsoleMoreMenu ──────────────────────────────────────────────── */
export function ConsoleMoreMenu({ commentsEnabled, onToggleComments, onOpenFx, recording, onStartRecording, onStopRecording, chatOpen, onToggleChat }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const itemClass = "w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-xs text-white text-left border-b border-white/5 last:border-b-0 transition";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="console-more-menu-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/5 text-white border border-white/10 hover:bg-white/10"
      >
        <MoreHorizontal className="w-3.5 h-3.5" /> More
      </button>
      {open && (
        <div data-testid="console-more-menu" className="absolute right-0 top-full mt-2 w-64 bg-[#0A0D14] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-50">
          <button data-testid="console-comments-toggle" onClick={() => { onToggleComments?.(); }} aria-pressed={!commentsEnabled} className={itemClass}>
            {commentsEnabled ? <MessageSquare className="w-4 h-4 text-tv-accent" /> : <MessageSquareOff className="w-4 h-4 text-red-400" />}
            <span className="flex-1">{commentsEnabled ? "Viewer comments ON" : "Viewer comments muted"}</span>
          </button>
          <button data-testid="console-fx-btn" onClick={() => { onOpenFx?.(); setOpen(false); }} className={itemClass}>
            <Wand2 className="w-4 h-4 text-tv-accent" />
            <span className="flex-1">Studio FX</span>
            <span className="text-[9px] text-white/40">Chroma · BG · Transitions</span>
          </button>
          {!recording ? (
            <>
              <button data-testid="record-option-local" onClick={() => { onStartRecording?.("local"); setOpen(false); }} className={itemClass}>
                <CircleIcon className="w-4 h-4 text-tv-live" /><span className="flex-1">Record → Hard Drive</span>
              </button>
              <button data-testid="record-option-cloud" onClick={() => { onStartRecording?.("cloud"); setOpen(false); }} className={itemClass}>
                <CircleIcon className="w-4 h-4 text-tv-accent" />
                <span className="flex-1">Record → HD + Cloud</span>
                <span className="text-[8px] bg-tv-accent text-black font-black px-1.5 py-0.5 rounded">PRO</span>
              </button>
              <button data-testid="record-option-live-cloud" onClick={() => { onStartRecording?.("live_cloud"); setOpen(false); }} className={itemClass}>
                <CircleIcon className="w-4 h-4 text-emerald-400" />
                <span className="flex-1">Record → Live Cloud</span>
                <span className="text-[8px] bg-emerald-500 text-black font-black px-1.5 py-0.5 rounded">PRO+</span>
              </button>
            </>
          ) : (
            <button data-testid="console-record-stop" onClick={() => { onStopRecording?.(); setOpen(false); }} className={itemClass + " bg-tv-live/15"}>
              <CircleIcon className="w-4 h-4 tv-pulse text-tv-live fill-current" />
              <span className="flex-1 font-black">Stop recording</span>
            </button>
          )}
          <button data-testid="console-chat-toggle" onClick={() => { onToggleChat?.(); setOpen(false); }} aria-pressed={chatOpen} className={itemClass}>
            <MessageSquare className={`w-4 h-4 ${chatOpen ? "text-tv-accent" : "text-white/70"}`} />
            <span className="flex-1">{chatOpen ? "Hide live chat" : "Open live chat"}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ── CrewModeSelect ──────────────────────────────────────────────── */
export function CrewModeSelect({ streamId }) {
  const [mode, setMode] = useState("active");
  useEffect(() => {
    if (!streamId) return;
    api.get(`/live/${streamId}/crew-mode`).then((r) => setMode(r.data?.crew_mode || "active")).catch(() => {});
  }, [streamId]);
  const pillColor =
    mode === "off"   ? "bg-white/5 text-white/60" :
    mode === "light" ? "bg-sky-500/20 text-sky-200 border-sky-500/30" :
                       "bg-tv-accent/20 text-tv-accent border-tv-accent/40";
  return (
    <div className="relative inline-flex items-center">
      <select
        data-testid="console-crew-mode"
        value={mode}
        onChange={async (e) => {
          const m = e.target.value;
          setMode(m);
          try {
            await api.post(`/live/${streamId}/crew-mode?mode=${m}`);
            toast.success(`Tap In Crew: ${m}`, { id: "crew-mode" });
          } catch {
            toast.error("Couldn't save crew mode", { id: "crew-mode" });
          }
        }}
        className={`appearance-none cursor-pointer text-xs font-bold px-2.5 py-1 pr-6 rounded border ${pillColor} focus:outline-none focus:border-tv-accent`}
        title="Tap In Crew engagement level for this stream"
      >
        <option value="off">Crew · Off</option>
        <option value="light">Crew · Light</option>
        <option value="active">Crew · Active</option>
      </select>
    </div>
  );
}
